import { CheckOutlined, CloseOutlined, ReloadOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import axios from "axios";
import { Modal, Space, Typography, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { BillingService } from "../../../api/billingService";
import { RefundRequest, RefundRequestStatus } from "../../../@type/billing.types";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import StatusBadge, { StatusBadgeTone } from "../../../components/common/StatusBadge";
import Card from "../../../components/common/Card";
import MuiTable from "../../../components/common/MuiTable";
import { Column } from "../../../utils/constants";

type Action = "complete" | "reject";

const OPEN_STATUSES: RefundRequestStatus[] = ["PENDING", "PROCESSING"];
const ALL_STATUSES: RefundRequestStatus[] = ["PENDING", "PROCESSING", "REFUNDED", "REJECTED", "FAILED"];

const STATUS_FILTER_WIDTH = 260;
const ROW_BUTTON_MIN_WIDTH = 90;

const STATUS_TONE: Record<RefundRequestStatus, StatusBadgeTone> = {
  PENDING: "warning",
  PROCESSING: "warning",
  REFUNDED: "success",
  REJECTED: "default",
  FAILED: "error",
};

const errorText = (error: unknown, fallback: string): string =>
  axios.isAxiosError(error) ? error.response?.data?.message ?? fallback : fallback;

const RefundRequests = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [rows, setRows] = useState<RefundRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [statuses, setStatuses] = useState<RefundRequestStatus[]>(OPEN_STATUSES);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<{ row: RefundRequest; action: Action } | null>(null);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await BillingService.getRefundRequests(statuses, page, size);
      setRows(response.data.data ?? []);
      setTotal(response.data.total ?? 0);
    } catch (error: unknown) {
      messageApi.error(errorText(error, "Failed to load refund requests"));
    } finally {
      setLoading(false);
    }
  }, [statuses, page, size, messageApi]);

  useEffect(() => {
    load();
  }, [load]);

  const openAction = (row: RefundRequest, action: Action) => {
    setReference("");
    setNote("");
    setTarget({ row, action });
  };

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const body = { provider_refund_id: reference || undefined, note: note || undefined };
      if (target.action === "complete") {
        await BillingService.completeRefundRequest(target.row.id, body);
      } else {
        await BillingService.rejectRefundRequest(target.row.id, body);
      }
      messageApi.success(target.action === "complete" ? "Marked as refunded" : "Request rejected");
      setTarget(null);
      await load();
    } catch (error: unknown) {
      messageApi.error(errorText(error, "Failed to update refund request"));
    } finally {
      setSaving(false);
    }
  };

  const header: Column[] = [
    { id: "id", label: "#", minWidth: 60, align: "left" },
    { id: "order_id", label: "Order", minWidth: 80, align: "left" },
    {
      id: "amount",
      label: "Refund",
      minWidth: 120,
      align: "right",
      format: (value: number, row: RefundRequest) => `${value} ${row.currency ?? ""}`,
    },
    {
      id: "fee_withheld",
      label: "Fee withheld",
      minWidth: 110,
      align: "right",
      format: (value: number) => String(value),
    },
    { id: "method", label: "Method", minWidth: 120, align: "center" },
    { id: "initiator", label: "Cancelled by", minWidth: 110, align: "center" },
    {
      id: "status",
      label: "Status",
      minWidth: 110,
      align: "center",
      format: (value: RefundRequestStatus) => (
        <StatusBadge label={value} tone={STATUS_TONE[value]} />
      ),
    },
    { id: "note", label: "Note", minWidth: 160, align: "left" },
    {
      id: "actions",
      label: "Actions",
      minWidth: 220,
      align: "center",
      format: (_: unknown, row: RefundRequest) =>
        row.status === "PENDING" || row.status === "PROCESSING" ? (
          <Space>
            <ActionButton
              sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }}
              startIcon={<CheckOutlined />}
              onClick={() => openAction(row, "complete")}
            >
              Paid
            </ActionButton>
            <CancelButton
              sx={{ minWidth: ROW_BUTTON_MIN_WIDTH }}
              startIcon={<CloseOutlined />}
              onClick={() => openAction(row, "reject")}
            >
              Reject
            </CancelButton>
          </Space>
        ) : null,
    },
  ];

  return (
    <Card className="user">
      {contextHolder}
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }} wrap>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Refund Requests
          </Typography.Title>
          <Space>
            <Box width={STATUS_FILTER_WIDTH}>
              <SelectField
                multiple
                value={statuses}
                options={ALL_STATUSES.map((status) => ({ label: status, value: status }))}
                onChange={(values) => {
                  setPage(0);
                  setStatuses(values.length ? (values as RefundRequestStatus[]) : OPEN_STATUSES);
                }}
              />
            </Box>
            <CancelButton appSize="large" startIcon={<ReloadOutlined />} onClick={load} loading={loading}>
              Reload
            </CancelButton>
          </Space>
        </Space>
        <MuiTable
          header={header}
          data={rows}
          isLoading={loading}
          page={page}
          rowsPerPage={size}
          totalElements={total}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(event) => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
        />
      </Space>

      <Modal
        title={target?.action === "complete" ? "Mark refund as paid" : "Reject refund request"}
        open={Boolean(target)}
        onCancel={() => setTarget(null)}
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <CancelButton onClick={() => setTarget(null)}>Cancel</CancelButton>
            <ActionButton onClick={submit} loading={saving}>
              {target?.action === "complete" ? "Confirm paid" : "Reject"}
            </ActionButton>
          </Box>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {target?.action === "complete" && (
            <InputField
              placeholder="Transfer code / voucher id (optional)"
              value={reference}
              inputProps={{ maxLength: 255 }}
              onChange={(event) => setReference(event.target.value)}
            />
          )}
          <InputField
            multiline
            minRows={3}
            placeholder="Note"
            value={note}
            inputProps={{ maxLength: 500 }}
            onChange={(event) => setNote(event.target.value)}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default RefundRequests;
